import React, { useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import OptionButton from 'components/shared/OptionButton';
import {
  selectCatalogsLoaded,
  selectOptionsLoaded,
  selectSubcatalogsLoaded,
} from 'selectors/loading_states';

import activeEditor from 'domains/active_editor';
import optionGroups from 'domains/option_groups';
import canvases from 'domains/canvases';

import type { OptionGroupAPIData, OptionAPIData } from 'domains/option_groups';
import type { OptionalPair, Pair } from 'types/common_types';

const optionButtonThrottle: { lastrun?: number; delay: number } = {
  lastrun: undefined,
  delay: 500,
};

type OptionButtonContainerProps = {
  option: OptionAPIData;
  optionGroup: OptionGroupAPIData;
  handleOpenTooltipModal: (id: number, targetId: string) => void;
};

function OptionButtonContainer({
  option,
  optionGroup,
  handleOpenTooltipModal = () => {},
}: OptionButtonContainerProps) {
  const dispatch = useAppDispatch();
  const catalogsLoaded = useAppSelector(selectCatalogsLoaded);
  const subcatalogsLoaded = useAppSelector(selectSubcatalogsLoaded);
  const optionsLoaded = useAppSelector(selectOptionsLoaded);
  const disabledOptions = useAppSelector(activeEditor.selectors.disabledOptions);
  const activeOptionConfig = useAppSelector(activeEditor.selectors.optionConfig);

  const isCustomProduct = useAppSelector(activeEditor.selectors.isCustomProduct);
  const activeCanvasCustomSizes = useAppSelector(activeEditor.selectors.customSize);

  const customHeight = useMemo(
    () => activeCanvasCustomSizes.height,
    [activeCanvasCustomSizes.height]
  );
  const customWidth = useMemo(() => activeCanvasCustomSizes.width, [activeCanvasCustomSizes.width]);

  const isLoading = useMemo(() => {
    return !catalogsLoaded || !subcatalogsLoaded || !optionsLoaded;
  }, [catalogsLoaded, subcatalogsLoaded, optionsLoaded]);

  const isSelected =
    !!activeOptionConfig[optionGroup.id] && activeOptionConfig[optionGroup.id] === option.id;
  const isRequired = optionGroup.required;

  function openTooltipModal(clickedOptionButtonId: string) {
    handleOpenTooltipModal(option.id, clickedOptionButtonId);
  }

  const disabled =
    disabledOptions[option.id] ||
    (isCustomProduct &&
      customHeight &&
      customWidth &&
      option.custom_options &&
      optionGroups.utils.customSizeOptionIsDisabled(
        customHeight,
        customWidth,
        option.custom_options
      ));

  const disabledTooltipDisplay = useMemo(() => {
    let str = '';

    if (disabled) {
      const customSize =
        isCustomProduct && customWidth && customHeight ? `${customWidth} x ${customHeight}` : '';
      str = `Not available with ${disabled.by || customSize}`;
    }

    if (option.unavailable) {
      str = 'This option is currently unavailable';
    }

    return str;
  }, [disabled, option, isCustomProduct, customHeight, customWidth]);

  // Until this is defined in the data, we need to cast it as a bool if undefined
  const unavailable = useMemo(() => !!option.unavailable, [option]);

  function handleClickOption(e: React.MouseEvent<HTMLButtonElement>) {
    const now = new Date().getTime();
    const { lastrun, delay } = optionButtonThrottle;
    if (!lastrun || now - lastrun >= delay) {
      optionButtonThrottle.lastrun = now;
      if (!isSelected) {
        const optionPair: Pair<number, number> = [optionGroup.id, option.id];
        dispatch(canvases.actions.changeActiveCanvasConfig.options({ options: [optionPair] }));
      } else if (!isRequired) {
        const optionPair: OptionalPair<number, number> = [optionGroup.id, undefined];
        dispatch(canvases.actions.changeActiveCanvasConfig.options({ options: [optionPair] }));
        e.currentTarget.blur();
      }
    } else {
      e.currentTarget.blur();
      e.preventDefault();
    }
  }
  return (
    <OptionButton
      option={option}
      isDisabled={!!disabled || option.read_only || isLoading || unavailable}
      disabledBy={disabledTooltipDisplay}
      isSelected={isSelected}
      onClick={handleClickOption}
      onClickTooltip={openTooltipModal}
    />
  );
}

export default OptionButtonContainer;
